// Share a booking link with a patient: copy it, send it from your own
// WhatsApp, or have MindConnect text it (training BE
// organization/booking_link_routes.py). Giving the patient's number records
// an invite, which takes them to this clinic's own page once they sign in.
import { useState } from "react";
import { X, Copy, MessageSquare, Send, ExternalLink } from "lucide-react";
import { toast } from "react-toastify";
import Portal from "../common/Portal";
import api from "../../api";

const SMS_RESULT = {
  sent: "SMS sent.",
  throttled: "This number has had several messages from us recently, so the SMS was held back. Try WhatsApp instead.",
  not_configured: "SMS isn't set up yet. Please send it on WhatsApp instead.",
  failed: "The SMS couldn't be sent. Please try WhatsApp instead.",
  skipped: "The SMS couldn't be sent. Please try WhatsApp instead.",
};

function digits10(value) {
  return (value || "").replace(/\D/g, "").slice(-10);
}

// Sends a booking link by WhatsApp (the staff member's own WhatsApp, message
// written) or SMS (from MindConnect), recording the invite. Shared with the
// patient list's "Share Appointment Link" dialog. Call it inside the click
// handler: it opens the WhatsApp tab straight away so the browser allows it.
// Returns true once the link went out (WhatsApp opened, or SMS sent).
export async function sendBookingLink({ channel, doctorUserId, phone }) {
  const waTab = channel === "whatsapp" ? window.open("about:blank", "_blank") : null;
  try {
    const res = await api.shareBookingLink({
      channel, doctor_user_id: doctorUserId || undefined, phone: phone || undefined,
    });
    if (channel === "whatsapp") {
      const to = digits10(phone).length === 10 ? `91${digits10(phone)}` : "";
      const url = `https://wa.me/${to}?text=${encodeURIComponent(res.data.message)}`;
      if (waTab) waTab.location.href = url; else window.open(url, "_blank", "noopener");
      return true;
    }
    const status = res.data.sms_status;
    (status === "sent" ? toast.success : toast.warn)(SMS_RESULT[status] || SMS_RESULT.failed);
    return status === "sent";
  } catch (e) {
    if (waTab) waTab.close();
    toast.error(e.response?.data?.error || "Couldn't share the link. Please try again.");
    return false;
  }
}

export default function ShareBookingLinkModal({ link, doctorName, doctorUserId, orgName, onClose }) {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(null);
  const valid = digits10(phone).length === 10;
  const hasPhone = phone.trim().length > 0;
  // The SMS template carries one doctor's link code only (DLT), so the clinic
  // page link goes by WhatsApp.
  const canSms = Boolean(doctorUserId);

  const copy = () => {
    navigator.clipboard?.writeText(link)
      .then(() => toast.success("Link copied"))
      .catch(() => toast.error("Couldn't copy the link."));
  };

  const share = async (channel) => {
    if (channel === "sms" && !valid) return;
    if (channel === "whatsapp" && hasPhone && !valid) return;
    setBusy(channel);
    const done = await sendBookingLink({ channel, doctorUserId, phone: hasPhone ? phone : undefined });
    setBusy(null);
    if (done && channel === "sms") onClose();
  };

  return (
    <Portal>
      <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between gap-3 p-5 border-b border-secondary-100">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-secondary-900">Share booking link</h2>
              <p className="text-sm text-secondary-500 truncate">{[doctorName, orgName].filter(Boolean).join(" · ")}</p>
            </div>
            <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-secondary-100" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="flex items-center gap-2 rounded-xl border border-secondary-200 bg-secondary-50 px-3 py-2">
              <span className="text-sm text-primary-700 break-all flex-1">{link}</span>
              <button type="button" onClick={copy} className="shrink-0 p-1.5 rounded-lg hover:bg-white" title="Copy link" data-label="Copy Booking Link">
                <Copy className="w-4 h-4" />
              </button>
              <a href={link} target="_blank" rel="noopener noreferrer" className="shrink-0 p-1.5 rounded-lg hover:bg-white" title="Preview">
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            <div>
              <label htmlFor="share-phone" className="block text-sm font-medium text-secondary-700 mb-1 normal-case font-sans tracking-normal">
                Patient's mobile number
              </label>
              <input id="share-phone" type="tel" inputMode="numeric" value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="10-digit mobile number" className="w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm" />
              {hasPhone && !valid && <p className="text-xs text-red-600 mt-1">Enter a 10-digit mobile number.</p>}
              <p className="text-xs text-secondary-500 mt-1">
                {canSms ? "Needed for SMS. For WhatsApp you can leave it blank and pick the contact in WhatsApp."
                  : "Optional: leave it blank and pick the contact in WhatsApp."}
              </p>
            </div>

            <div className={`grid gap-2 ${canSms ? "grid-cols-2" : "grid-cols-1"}`}>
              <button type="button" onClick={() => share("whatsapp")} disabled={busy !== null || (hasPhone && !valid)}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                data-label="Share Link WhatsApp">
                <MessageSquare className="w-4 h-4" /> {busy === "whatsapp" ? "Opening…" : "WhatsApp"}
              </button>
              {canSms && <button type="button" onClick={() => share("sms")} disabled={busy !== null || !valid}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                data-label="Share Link SMS">
                <Send className="w-4 h-4" /> {busy === "sms" ? "Sending…" : "Send SMS"}
              </button>}
            </div>
            <p className="text-xs text-secondary-500">
              WhatsApp goes from your own WhatsApp with the link in the message.
              {canSms ? " The SMS comes from MindConnect with this doctor's link." : " To text a link, share a doctor's link instead."}
            </p>
          </div>
        </div>
      </div>
    </Portal>
  );
}
